import * as React from 'react';

import { computeText } from '@/locales/utils';
import { Input } from '@/components/form/Input';
import { FormattedMessage, useIntl } from 'react-intl';
import { Text } from '@/components/atoms/Text';
import { SvyButton } from '@/components/savory/SvyButton';
import { confirm } from '@/core/asyncActions/confirm';
import { useCopySchedulesMutation } from '@/services/innovorder/adminTools/adminTools.endpoints';
import { FormControl, FormControls, SuccessMessage } from '../AdminTools.style';
import { safeMutate } from '@/utils/errors';
import { Form } from '@/core/form/Form';
import { mustMatchValue } from '@/utils/form/validate';

export type CopySchedulesInputs = {
    scheduleType: string;
    idSource: string;
    idsDestination: string;
};

const CopySchedules: React.FunctionComponent = () => {
    const intl = useIntl();

    const [copySchedules, { isSuccess }] = useCopySchedulesMutation();

    const handleSubmitForm = async ({ scheduleType, idSource, idsDestination }: CopySchedulesInputs) => {
        const hasConfirmed = await confirm(computeText(intl, 'adminTools.confirmModal.description'));
        if (hasConfirmed) {
            await safeMutate(
                copySchedules({
                    scheduleType,
                    idSource,
                    idsDestination,
                }),
            );
        }
    };

    return (
        <Form<CopySchedulesInputs>>
            {({ handleSubmit }) => {
                return (
                    <>
                        <Text type="large" text="brand.adminTools.copySchedules.title" />
                        <FormControls>
                            <FormControl>
                                <Input
                                    name="scheduleType"
                                    rules={{
                                        required: computeText(intl, 'field.required'),
                                        validate: (value = '') => mustMatchValue('pickup', 'cel')(intl)(value ?? ''),
                                    }}
                                    placeholder="brand.adminTools.copySchedules.form.placeholder.scheduleType"
                                    labelId="brand.adminTools.copySchedules.form.scheduleType"
                                />
                                <Input
                                    name="idSource"
                                    rules={{ required: computeText(intl, 'field.required') }}
                                    placeholder="brand.adminTools.copySchedules.form.placeholder.idSource"
                                    labelId="brand.adminTools.copySchedules.form.idSource"
                                />
                                <Input
                                    name="idsDestination"
                                    rules={{ required: computeText(intl, 'field.required') }}
                                    placeholder="brand.adminTools.copySchedules.form.placeholder.idsDestination"
                                    labelId="brand.adminTools.copySchedules.form.idsDestination"
                                />
                            </FormControl>
                            <SvyButton onClick={handleSubmit(handleSubmitForm)}>
                                <FormattedMessage id="button.validate" />
                            </SvyButton>
                            {isSuccess && <SuccessMessage text="brand.adminTools.copySchedules.form.success" />}
                        </FormControls>
                    </>
                );
            }}
        </Form>
    );
};

export default CopySchedules;
